import { useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import {
  createPublicClient, createWalletClient, custom, http, erc20Abi, parseUnits, formatUnits,
  type Address,
} from "viem";
import { CADENAS, LIFI, cadena, esNativo, type Cotizacion, type TokenPuente } from "../lib/puente";
import { traducir } from "../lib/errores";

const MULTICALL3 = "0xcA11bde05977b3631167028862bE2a173976CA11" as const;

export const cliente = (id: number) => {
  const c = cadena(id)!;
  return createPublicClient({
    chain: {
      id: c.id, name: c.nombre, nativeCurrency: c.moneda,
      rpcUrls: { default: { http: [c.rpc] } },
      contracts: { multicall3: { address: MULTICALL3 } },
    },
    transport: http(c.rpc),
  });
};

const PRIORIDAD = ["USDG", "USDC", "USDT", "DAI", "WETH", "ETH"];

// SOLO lo que hace falta para operar: el gas de cada red (ETH/HYPE/BNB) y los estables de los pares
// (USDC/USDG). LI.FI devuelve cientos de tokens por cadena —memecoins, wrapped raros, stables
// puenteados— que no se usan para abrir una piscina y solo son ruido en el SELECTOR DEL PUENTE. Se
// aceptan también las formas wrapped del nativo (WETH/WHYPE/WBNB): son el mismo activo y es lo que el
// vault deposita en el lado volátil.
//
// El filtro se aplica en `useTokens` (la vista del puente), NO en `traerTokens`: el barredor de
// polvo (usePolvo) reutiliza la MISMA lista cacheada para encontrar restos sueltos en cualquier
// token, y ahí sí hace falta la lista completa. Filtrar en origen lo dejaría ciego.
const PUENTE_PERMITIDOS = new Set(["ETH", "WETH", "USDG", "USDC", "HYPE", "WHYPE", "BNB", "WBNB"]);
export const esTokenOperable = (t: TokenPuente) => PUENTE_PERMITIDOS.has((t.symbol ?? "").toUpperCase());

/** La lista COMPLETA de tokens de una cadena, de LI.FI. Se cachea: son cientos y no cambian. La
 *  comparten el selector del puente (filtrada) y el barredor de polvo (entera). */
export async function traerTokens(idCadena: number): Promise<TokenPuente[]> {
  const j = await fetch(`${LIFI}/tokens?chains=${idCadena}`).then((r) => r.json());
  const lista = (j?.tokens?.[idCadena] ?? []) as TokenPuente[];
  return [...lista].sort((a, b) => {
    const ia = PRIORIDAD.indexOf((a.symbol ?? "").toUpperCase());
    const ib = PRIORIDAD.indexOf((b.symbol ?? "").toUpperCase());
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
}

export const CLAVE_TOKENS = (idCadena: number) => ["tokens-puente", idCadena] as const;

export function useTokens(idCadena: number) {
  return useQuery({
    queryKey: CLAVE_TOKENS(idCadena),
    staleTime: 60 * 60 * 1000,
    queryFn: () => traerTokens(idCadena),
    // `select` filtra SOLO la vista del puente sin tocar la caché: el polvo (que lee la misma
    // queryKey con getQueryData/fetchQuery) sigue viendo la lista entera.
    select: (todos) => todos.filter(esTokenOperable),
  });
}

/**
 * Los tokens que el usuario TIENE en esa cadena, con saldo, en una sola llamada (Multicall3).
 *
 * Enseñar los cientos de tokens de la cadena y que el usuario busque el suyo es la peor version de
 * esta pantalla: elige uno que no tiene, cotiza, firma el approve y la transaccion revierte.
 *
 * De cada token se guarda el saldo DOS veces: humano para enseñarlo y crudo para gastarlo. Ver
 * `TokenPuente.crudo` — rehacer el crudo a partir del humano pide de mas y revierte.
 */
export async function leerSaldos(
  idCadena: number, cuenta: Address, tokens: TokenPuente[]
): Promise<TokenPuente[]> {
  const client = cliente(idCadena);
  const tenidos: TokenPuente[] = [];
  const precio = (t: TokenPuente) => parseFloat(t.priceUSD ?? "0") || 0;

  const nativo = tokens.find((t) => esNativo(t.address));
  if (nativo) {
    const b = await client.getBalance({ address: cuenta }).catch(() => 0n);
    if (b > 0n) tenidos.push({ ...nativo, saldo: Number(formatUnits(b, 18)), crudo: b.toString() });
  }

  const ercs = tokens.filter((t) => !esNativo(t.address));
  for (let i = 0; i < ercs.length; i += 120) {
    const trozo = ercs.slice(i, i + 120);
    const res = await client.multicall({
      allowFailure: true,
      contracts: trozo.map((t) => ({
        address: t.address, abi: erc20Abi, functionName: "balanceOf" as const, args: [cuenta],
      })),
    }).catch(() => null);
    if (!res) continue;
    res.forEach((r, j) => {
      const t = trozo[j]!;
      if (r.status === "success" && r.result > 0n) {
        const s = Number(formatUnits(r.result, t.decimals ?? 18));
        if (s > 0) tenidos.push({ ...t, saldo: s, crudo: r.result.toString() });
      }
    });
  }

  return tenidos.sort(
    (a, b) => (b.saldo ?? 0) * precio(b) - (a.saldo ?? 0) * precio(a) || (b.saldo ?? 0) - (a.saldo ?? 0)
  );
}

export function useSaldos(idCadena: number, tokens: TokenPuente[] | undefined) {
  const { address } = useAccount();
  return useQuery({
    queryKey: ["saldos-puente", idCadena, address, tokens?.length],
    enabled: !!address && !!tokens?.length,
    staleTime: 30_000,
    queryFn: () => leerSaldos(idCadena, address!, tokens!),
  });
}

export type Estado = {
  fase: string;
  error?: string;
  hash?: string;
  hecho?: boolean;
  /** la cadena donde se firmo, para poder enlazar al explorador correcto */
  cadena?: number;
};

/** La wallet inyectada, en un solo sitio para no repartir el `any` por todo el archivo. */
export const walletDelNavegador = () => (window as { ethereum?: any }).ethereum;

/** Pone la wallet en la cadena de origen, dandola de alta si no la conoce. */
export async function cambiarRed(eth: any, idCadena: number) {
  const c = cadena(idCadena)!;
  const hex = `0x${idCadena.toString(16)}`;
  try {
    await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: hex }] });
  } catch (e: any) {
    if (e?.code === 4902) {
      await eth.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: hex, chainName: c.nombre, rpcUrls: [c.rpc],
          nativeCurrency: c.moneda, blockExplorerUrls: [c.explorador],
        }],
      });
    } else throw e;
  }
}

/**
 * base fee x1,3 + 0,1 gwei. Sin colchon, Robinhood y HyperEVM rechazan con "max fee per gas less
 * than block base fee".
 */
export async function precioDeGas(idCadena: number): Promise<bigint | undefined> {
  try {
    const bloque = await cliente(idCadena).getBlock();
    if (bloque.baseFeePerGas) return (bloque.baseFeePerGas * 13n) / 10n + 100_000_000n;
  } catch { /* la cadena dira su precio */ }
  return undefined;
}

/**
 * Firma y espera una cotizacion ya hecha. Vive suelta —y no dentro del hook— porque el barrido de
 * polvo manda decenas de estas seguidas y necesita EXACTAMENTE las mismas guardas, que vienen de
 * fallos que costaron gas de verdad:
 *
 *  · El saldo se relee EN LA CADENA justo antes de firmar. El desplegable guarda el saldo del
 *    momento en que se cargo la lista; si cambio, el puente pedia mas de lo que hay y la
 *    transaccion revertia despues de que el usuario ya hubiera pagado.
 *  · `gasPrice` con colchon, ver `precioDeGas`.
 *  · Cada paso se SIMULA antes de firmarlo. Una transaccion que revierte cuesta el gas igual.
 */
export async function enviarCotizacion(
  q: Cotizacion, deCadena: number, deToken: Address, cuenta: Address,
  avisar: (fase: string, hash?: string) => void
): Promise<`0x${string}` | undefined> {
  const eth = walletDelNavegador();
  if (!eth) throw new Error("No encuentro tu wallet en este navegador.");

  const c = cadena(deCadena)!;
  avisar("Cambiando a la red de origen…");
  await cambiarRed(eth, deCadena);

  const publico = cliente(deCadena);
  const wallet = createWalletClient({ account: cuenta, transport: custom(eth) });
  const gasPrice = await precioDeGas(deCadena);

  // LI.FI entrega solo la tx del puente; la aprobacion la hacemos nosotros. Relay ya la trae.
  const necesita = BigInt(q.desdeImporte || 0);
  if (q.fuente === "lifi" && q.direccionAprobacion && !esNativo(deToken)) {
    const permitido = await publico.readContract({
      address: deToken, abi: erc20Abi, functionName: "allowance",
      args: [cuenta, q.direccionAprobacion],
    });
    if (permitido < necesita) {
      avisar("Aprobando el token en tu wallet…");
      const h = await wallet.writeContract({
        address: deToken, abi: erc20Abi, functionName: "approve",
        args: [q.direccionAprobacion, necesita],
        chain: null, ...(gasPrice ? { gasPrice } : {}),
      });
      { const r = await publico.waitForTransactionReceipt({ hash: h });
        if (r.status !== "success") throw new Error("Una transacción del puente revirtió."); }
    }
  }

  // El saldo REAL, ahora, en la cadena de origen.
  if (necesita > 0n) {
    const tiene = esNativo(deToken)
      ? await publico.getBalance({ address: cuenta })
      : await publico.readContract({
          address: deToken, abi: erc20Abi, functionName: "balanceOf", args: [cuenta],
        });
    if (tiene < necesita) {
      throw new Error(
        `En ${c.nombre} solo tienes ${tiene} de ese token y el puente pide ${necesita}. ` +
        `Pulsa MÁX para reajustar el importe.`
      );
    }
  }

  let ultimo: `0x${string}` | undefined;
  for (const paso of q.pasos) {
    avisar(paso.id === "approve" ? "Aprueba el token en tu wallet…" : "Confirma el envío en tu wallet…");
    const tx = {
      to: paso.to, data: paso.data, value: paso.value,
      ...(paso.gas ? { gas: paso.gas } : {}),
      ...(gasPrice ? { gasPrice } : {}),
    };
    // Simular antes de firmar: un revert cuesta el gas igual.
    await publico.call({ account: cuenta, ...tx });
    ultimo = await wallet.sendTransaction({ account: cuenta, chain: null, ...tx });
    avisar("Esperando a la red…", ultimo);
    { const r = await publico.waitForTransactionReceipt({ hash: ultimo });
      if (r.status !== "success") throw new Error("La transacción del puente revirtió."); }
  }
  return ultimo;
}

/**
 * EL SALDO YA NO ES EL DE ANTES. Sin invalidar, la pantalla se queda enseñando el saldo de hace un
 * momento y el usuario cree que la operacion no ha hecho nada — o peor, la repite. La cotizacion
 * tambien: la ruta que se acaba de gastar ya no vale para nada.
 */
export function refrescarSaldos(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ["saldos-puente"] }),
    qc.invalidateQueries({ queryKey: ["cartera-puente"] }),
    qc.invalidateQueries({ queryKey: ["cotizar"] }),
  ]);
}

export function usePuente() {
  const { address } = useAccount();
  const qc = useQueryClient();
  // En desarrollo se puede forzar el estado final con ?hecho=1, para poder MIRAR la pantalla de
  // exito sin gastar dinero. `import.meta.env.DEV` es constante al compilar: no viaja a produccion.
  const [estado, setEstado] = useState<Estado | null>(
    import.meta.env.DEV && new URLSearchParams(location.search).get("hecho")
      ? { fase: "hecho", hecho: true, hash: "0x7b61c7322d0000000000000000000000000000000000000000000000000000dead", cadena: 4663 }
      : null
  );

  async function ejecutar(q: Cotizacion, deCadena: number, deToken: Address) {
    if (!address) return;
    setEstado({ fase: "Cambiando a la red de origen…" });
    try {
      const hash = await enviarCotizacion(q, deCadena, deToken, address, (fase, h) =>
        setEstado({ fase, ...(h ? { hash: h, cadena: deCadena } : {}) })
      );
      await refrescarSaldos(qc);
      setEstado({ fase: "hecho", hash, hecho: true, cadena: deCadena });
    } catch (e) {
      // La cadena de ORIGEN: es la que paga el gas, y la que hay que nombrar si falta.
      const c = cadena(deCadena);
      setEstado({ fase: "", error: traducir(e, { moneda: c?.moneda.symbol, red: c?.nombre }) });
    }
  }

  return { estado, ejecutar, limpiar: () => setEstado(null) };
}

// La traduccion de errores vive en `lib/errores.ts`: habia una copia aqui y otra en useGobierno.ts
// con casos distintos, y cada una dejaba en crudo lo que la otra traducia. Se reexporta porque las
// pantallas del puente ya la importaban de aqui.
export { traducir };

export { CADENAS, parseUnits };
